import React, { useState } from 'react'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Loader2, CheckCircle, Database, ExternalLink } from 'lucide-react'
import GoogleDriveLogo from '/assets/image/google-drive.svg'
import { useListGoogleDrives, useDeleteGoogleDrive } from '@/services/storage'
import { GoogleDrive } from '@/services/inference.gen'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'react-hot-toast'

export const ListGoogleDrives = () => {
  const [open, setOpen] = useState(false)

  const {
    data: googleDrivesData,
    isLoading,
    isError,
    error,
  } = useListGoogleDrives()

  const { mutateAsync: deleteDrive, isPending: isDeleting } =
    useDeleteGoogleDrive()

  const handleDeleteDrive = async (drive: GoogleDrive) => {
    try {
      await deleteDrive({ id: drive.id })
      toast.success('Google Drive connection removed')
    } catch (error) {
      toast.error('Failed to remove Google Drive connection')
    }
  }

  const formatDriveTitle = (url: string) => {
    // Extract meaningful title from Google Drive URL
    if (url.includes('drive.google.com/drive/folders/')) {
      return 'Google Drive Folder'
    }
    return 'Google Drive'
  }

  const formatUrl = (url: string) => {
    // Show a cleaner version of the URL
    try {
      const urlObj = new URL(url)
      return `${urlObj.hostname}${urlObj.pathname}`
    } catch {
      return url
    }
  }

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline">List Google Drives</Button>
        </DialogTrigger>

        <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <img
                src={GoogleDriveLogo}
                alt="Google Drive"
                className="w-5 h-5"
              />
              All Google Drives
            </DialogTitle>
            <DialogDescription>
              Loading your connected Google Drive folders...
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {Array.from({ length: 2 }).map((_, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded" />
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-4 w-[160px]" />
                      <Skeleton className="h-3 w-[120px]" />
                    </div>
                    <Skeleton className="h-8 w-[60px]" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  if (isError) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline">List Google Drives</Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Unable to Load Google Drives</DialogTitle>
            <DialogDescription>
              We couldn't load your Google Drive connections. Please try again.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mt-4">
            <p className="text-destructive text-sm">
              {error?.message || 'An error occurred'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  const googleDrives =
    googleDrivesData?.items.filter((drive) => !drive.is_deleted) || []

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">List Google Drives</Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-[700px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <img src={GoogleDriveLogo} alt="Google Drive" className="w-5 h-5" />
            All Google Drives
          </DialogTitle>
          <DialogDescription>
            All your connected Google Drive folders.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {googleDrives.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <img
                  src={GoogleDriveLogo}
                  alt="Google Drive"
                  className="w-10 h-10"
                />
              </div>
              <h3 className="text-lg font-semibold foreground mb-2">
                No Google Drives Connected
              </h3>
              <p className="text-foreground/60 mb-6 max-w-sm">
                Connect your Google Drive folders when creating storage volume
                to sync files.
              </p>
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              {googleDrives.map((drive: GoogleDrive) => {
                const filteredStorageVolumes =
                  drive.storage_volumes?.filter(
                    (volume) => !volume.is_deleted
                  ) || []

                return (
                  <Card
                    key={drive.id}
                    className="border-2 hover:border-primary/30 transition-colors"
                  >
                    <CardContent className="p-5">
                      {/* Main Drive Info */}
                      <div className="flex items-start gap-4">
                        <div className="relative">
                          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                            <img
                              src={GoogleDriveLogo}
                              alt="Google Drive"
                              className="w-7 h-7"
                            />
                          </div>
                          {filteredStorageVolumes.length > 0 && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                              <CheckCircle className="w-3 h-3 text-primary-foreground" />
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-foreground">
                              {formatDriveTitle(drive.url)}
                            </h3>
                            {drive.storage_volumes &&
                              drive.storage_volumes.length > 0 && (
                                <Badge className="bg-primary/10 text-primary border-primary/20">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Connected
                                </Badge>
                              )}
                          </div>

                          <a
                            href={drive.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm text-foreground/60 mb-2 hover:text-foreground/80 transition-colors cursor-pointer"
                          >
                            <ExternalLink className="w-3 h-3" />
                            <span className="truncate">
                              {formatUrl(drive.url)}
                            </span>
                          </a>

                          <div className="flex items-center gap-2 mb-2">
                            <Badge
                              variant="outline"
                              className="text-xs bg-card"
                            >
                              Created{' '}
                              {new Date(drive.created_at).toLocaleDateString(
                                'en-US',
                                {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                }
                              )}
                            </Badge>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (filteredStorageVolumes.length > 0) {
                                toast.custom(
                                  <div className="bg-primary/10 border border-primary/20 backdrop-blur-2xl rounded-lg p-4">
                                    <p className="text-primary text-sm">
                                      Cannot remove Google Drive. Please delete
                                      the {filteredStorageVolumes.length}{' '}
                                      attached storage volume
                                      {filteredStorageVolumes.length > 1
                                        ? 's'
                                        : ''}{' '}
                                      first.
                                    </p>
                                  </div>
                                )
                                return
                              }

                              handleDeleteDrive(drive)
                            }}
                            disabled={isDeleting}
                            className="text-destructive border-destructive/20 hover:bg-destructive/10 hover:border-destructive/30"
                          >
                            {isDeleting ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              'Remove'
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Storage Volumes Section */}
                      {filteredStorageVolumes.length > 0 ? (
                        <div className="mt-4 pt-4 border-t border-border">
                          <div className="flex items-center gap-2 mb-3">
                            <Database className="w-4 h-4 text-primary" />
                            <span className="text-sm font-medium text-foreground/80">
                              Storage Volumes ({filteredStorageVolumes.length})
                            </span>
                          </div>

                          <div className="grid gap-3">
                            {filteredStorageVolumes.map((volume) => {
                              return (
                                <div
                                  key={volume.volume_id}
                                  className="bg-primary/5 flex w-full items-center justify-between border border-primary/10 rounded-lg p-3"
                                >
                                  <h4 className="font-medium text-foreground text-sm capitalize">
                                    {volume.volume_name}
                                  </h4>

                                  <div className="flex items-center gap-2 mt-1">
                                    <Badge
                                      variant="outline"
                                      className="text-xs bg-card"
                                    >
                                      {volume.size_gb}GB
                                    </Badge>
                                    {volume.is_public && (
                                      <Badge
                                        variant="secondary"
                                        className="text-xs"
                                      >
                                        Public
                                      </Badge>
                                    )}
                                    {volume.read_only && (
                                      <Badge
                                        variant="outline"
                                        className="text-xs"
                                      >
                                        Read Only
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-4 pt-4 border-t border-border">
                          <p className="text-sm text-foreground/60">
                            No storage volumes attached to this drive.
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
